/**
 * CoachFab — the persistent "Ask a coach" entry point (the coaches MOAT).
 *
 * The AI coaches live at `/v2/chat`, which opts out of the global nav. Mobile
 * had no durable way to reach them, so this floating action button surfaces
 * them on every in-app browsing screen. Chosen over a 6th bottom-tab: the coach
 * is the primary ACTION (FAB semantics), keeps the bottom nav at a clean 5, and
 * is reachable from any screen. Desktop (≥1024) uses the SideNav's elevated
 * "Ask a coach" action instead, so this self-gates to ≤1023px.
 *
 * Design polish (Claude Design pass, 2026-06-19):
 *  - collapses to a 48px icon on scroll-down, re-expands on scroll-up / at rest
 *    (snaps, no transition, under prefers-reduced-motion);
 *  - a one-time first-run coach-mark ("Your coaches live here"), dismissed on
 *    first tap or first scroll and never shown again (localStorage flag);
 *  - hidden on the focused full-screen opt-out surfaces (`/chat`, `/plan`) and
 *    the chrome-free flows (landing, onboarding).
 *
 * Today 2.0 (Claude Design return, 2026-10-18, item 4): with `today_2_0_v1` on, ON
 * `/today` the button is the 48px icon whenever the page is scrolled past
 * `TODAY_ICON_SCROLL_Y` and is the pill again only at the top. That is the scroll
 * POSITION, not its direction. Every other route, and Today with the flag off, keeps
 * the direction rule above; the first-run cue still forces the pill; the label is the
 * accessible name in both shapes. Pages reserve room for the button with the
 * `--fab-clearance` token (tokens.css).
 */
import { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useScrollDirection } from '../../hooks/useScrollDirection';
import { useServerFlag } from '../../hooks/useServerFlag';
import { useAuthContext } from '../../contexts/AuthContext';
import { useGuardianMode } from '../../hooks/useGuardianMode';
import { BOOT_CHROME_FREE } from '../../lib/nav/bootChromeFree';
import styles from './CoachFab.module.css';

/**
 * Surfaces where the FAB must not appear (paths are basename-stripped):
 *  - `/chat` — already at the destination.
 *  - `/plan` — focused full-screen opt-out flow (own RouteBack), kept distraction-free.
 *  - `/races` — already mounts its OWN primary "Log a race" FAB in the same
 *    bottom-right slot; we yield rather than collide (the handoff matrix wanted
 *    the coach here, but didn't account for the races FAB — flagged for design).
 *  - `/settings` — a config surface (not a coaching browse screen); the FAB sat
 *    over the Account → Sign out row in the same bottom-right slot (founder,
 *    2026-07-07). Yield the slot like `/races`.
 *  - `/landing`, `/onboarding` — chrome-free marketing / auth-flow surfaces.
 *  - `/run` (mobile Part B) — immersive live-run surface; the FAB
 *    overlapped its controls on device.
 *  - `/welcome` (mobile v1 D5) — the shell's signed-out entry surface.
 *  - `/` (BOOT §1) — the native boot splash; the FAB must not paint over the
 *    launch celebration. Comes from the shared BOOT_CHROME_FREE list.
 */
const HIDDEN_PATHS: ReadonlyArray<string> = ['/chat', '/plan', '/races', '/settings', ...BOOT_CHROME_FREE];
/** localStorage flag — gates the one-time first-run coach-mark. */
const SEEN_KEY = 'tayro.coachFab.seen';
/** Today 2.0: the page is "scrolled" (button is the icon) above this many px of scrollY. */
const TODAY_ICON_SCROLL_Y = 24;
/** Today 2.0: the extra read this long after the last scroll event, so the shape matches where the page came to rest. */
const SCROLL_SETTLE_MS = 120;

export function CoachFab(): JSX.Element | null {
  const isBelowDesktop = useMediaQuery('(max-width: 1023px)');
  const { user } = useAuthContext();
  // UAT-B4 — a guardian has no coach relationship; the "Ask a coach" FAB must
  // never mount on ANY route for a guardian-only account (gate here, not by
  // growing HIDDEN_PATHS). Coach-only accounts KEEP the FAB — their chat entry
  // is now role-aware.
  const { guardianOnly } = useGuardianMode();
  const location = useLocation();
  const scrollDir = useScrollDirection();
  const todayV2On = useServerFlag('today_2_0_v1');
  const [showCue, setShowCue] = useState(false);
  // Today 2.0 (item 4): the icon follows the scroll POSITION on /today, flag on only.
  const pathname = location.pathname;
  // The router matches paths without regard to case, so the Today test does too.
  const onToday = /^\/today(\/|$)/i.test(pathname);
  const positionRule = todayV2On && onToday;
  const [scrolledPast, setScrolledPast] = useState(false);

  // W2-UX-FIX2 (pixel check 2026-10-18): the switch lagged 1 to 3.5 s now and then. The shape is a function of the
  // scroll POSITION, but it was only ever re-read when a scroll event arrived, and a position can change without
  // one that this listener sees: the last event of a gesture delivered late or dropped (momentum ends, a busy main
  // thread while the page behind loads), a tab or an app that comes back with the page already scrolled or
  // restored, a resize that changes the page height. So the position is re-read (a) when the rule starts, before
  // the first paint (layout effect), (b) on every scroll event, (c) once more shortly after the last scroll
  // event, and (d) whenever the page comes back (visibility, pageshow, focus) or is resized. Every read is the same
  // pure read of `window.scrollY`, so an extra one can only correct the shape, never flip it wrongly.
  useLayoutEffect(() => {
    if (!positionRule) {
      setScrolledPast(false);
      return;
    }
    const read = () => setScrolledPast(window.scrollY > TODAY_ICON_SCROLL_Y);
    let settle: ReturnType<typeof setTimeout> | undefined;
    const onScroll = () => {
      read();
      clearTimeout(settle);
      settle = setTimeout(read, SCROLL_SETTLE_MS);
    };
    const onReturn = () => {
      if (document.visibilityState !== 'hidden') read();
    };
    read();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', read);
    window.addEventListener('pageshow', onReturn);
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      clearTimeout(settle);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', read);
      window.removeEventListener('pageshow', onReturn);
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [positionRule]);

  // First-run cue: show once, until dismissed (tap or first scroll).
  useEffect(() => {
    try {
      if (!localStorage.getItem(SEEN_KEY)) setShowCue(true);
    } catch {
      /* localStorage unavailable (private mode) — skip the cue. */
    }
  }, []);

  useEffect(() => {
    if (showCue && scrollDir !== 'idle') dismissCue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollDir]);

  function dismissCue() {
    setShowCue(false);
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      /* no-op */
    }
  }

  // Desktop uses the SideNav action; signed-out users have no app to chat in.
  if (!isBelowDesktop || !user) return null;
  // UAT-B4 — guardian-only accounts never get the coach FAB.
  if (guardianOnly) return null;

  if (HIDDEN_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))) {
    return null;
  }

  // Collapse to an icon on scroll-down — but never while the first-run cue is up.
  // Today 2.0: on /today the icon follows the scroll position instead of its direction.
  const collapsed = !showCue && (positionRule ? scrolledPast : scrollDir === 'down');

  return (
    <div className={styles.wrap}>
      {showCue && (
        <div className={styles.cue} role="status">
          Your coaches live here
        </div>
      )}
      <Link
        to="/chat"
        className={[styles.fab, collapsed ? styles.collapsed : ''].filter(Boolean).join(' ')}
        aria-label="Ask a coach"
        onClick={dismissCue}
      >
        <span className={styles.glyph} aria-hidden="true">
          <svg
            width="17"
            height="17"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 9a2 2 0 0 1-2 2H6.5L3.5 13.5V11H4a2 2 0 0 1-2-2V4.5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z" />
          </svg>
        </span>
        <span className={styles.label}>Ask a coach</span>
      </Link>
    </div>
  );
}
